import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@loader/ui';
import Nango, { AuthError } from '@nangohq/frontend';
import { useQueryClient } from '@tanstack/react-query';
import type { HubspotObjectType } from '@loader/shared-types';

import { IntegrationsList } from './components/IntegrationsList';
import { IntegrationPickerDialog } from './components/IntegrationPickerDialog';
import { CredentialsDialog } from './components/CredentialsDialog';
import type { CredentialsResult } from './components/CredentialsDialog';
import { HttpFetchDialog } from './components/HttpFetchDialog';
import { SqlFetchDialog } from './components/SqlFetchDialog';
import { PostApiDialog } from './components/PostApiDialog';
import { useConnectorListQuery } from './hooks/useBaseConnectors';
import {
  useNangoSessionToken,
  useCreateFromConnection,
  useDisconnectIntegration,
  useWorkspaceConnectionsQuery,
} from './hooks/useNangoConnectors';
import type { NangoIntegration } from './hooks/useNangoConnectors';
import { useCreateCustomConnector } from './hooks/useCustomConnectors';
import { useHubspotOAuthConnect, useBatchCreateHubspot } from './hooks/useHubspotAuth';
import { useIntegrationConnectionsQuery, useDisconnectIntegrationConnection } from './hooks/useIntegrationConnections';
import type { ConnectorType } from './types';

import { queryKeys } from '@/lib/queryKeys';
import { useTablesListQuery } from '@/features/tables/hooks/useTableQueries';
import { useToastStore } from '@/store';
import { useWorkspaceMembership } from '@/hooks';
import { AccessRestricted } from '@/features/settings/components/AccessRestricted';

/** Providers that need headless auth (credentials form) instead of OAuth popup */
function getAuthKind(provider: string): 'oauth' | 'api_key' | 'basic' {
  if (provider.includes('basic')) return 'basic';
  if (provider.includes('private-api') || provider.includes('api-key')) return 'api_key';
  return 'oauth';
}

export function InputsPage() {
  const { canEdit, isLoading: membershipLoading } = useWorkspaceMembership();
  const toast = useToastStore();
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [credentialsTarget, setCredentialsTarget] = useState<NangoIntegration | null>(null);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [httpFetchOpen, setHttpFetchOpen] = useState(false);
  const [sqlFetchOpen, setSqlFetchOpen] = useState(false);
  const [postApiOpen, setPostApiOpen] = useState(false);

  const { data: tables = [], isLoading: tablesLoading, error: tablesError } = useTablesListQuery();
  const { data: connectors = [], isLoading: connectorsLoading, error: connectorsError } = useConnectorListQuery();
  const { data: nangoConnections } = useWorkspaceConnectionsQuery();
  const { data: integrationConnections } = useIntegrationConnectionsQuery();
  const { startOAuth: startHubspotOAuth, error: hubspotOAuthError } = useHubspotOAuthConnect();
  const batchCreateHubspot = useBatchCreateHubspot();

  // Derive connected provider keys from workspace connections
  const connectedProviderKeys = useMemo(
    () => new Set((nangoConnections ?? []).map((c) => c.providerConfigKey)),
    [nangoConnections]
  );

  // Derive connected integration providers (e.g. 'hubspot')
  const connectedIntegrationProviders = useMemo(
    () => new Set((integrationConnections ?? []).map((c) => c.provider)),
    [integrationConnections]
  );

  const sessionTokenMutation = useNangoSessionToken();
  const createFromConnection = useCreateFromConnection();
  const createCustomConnector = useCreateCustomConnector();
  const disconnectIntegration = useDisconnectIntegration();
  const disconnectNativeConnection = useDisconnectIntegrationConnection();

  useEffect(() => {
    if (hubspotOAuthError) toast.error(hubspotOAuthError);
  }, [hubspotOAuthError, toast]);

  const connectWithOAuth = useCallback(
    async (integration: NangoIntegration) => {
      try {
        const token = await sessionTokenMutation.mutateAsync(undefined);
        const nango = new Nango({ connectSessionToken: token });

        await nango.auth(integration.uniqueKey, { detectClosedAuthWindow: true });
        void queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
        toast.success('Integration connected — select syncs to activate');
      } catch (error: unknown) {
        if (error instanceof AuthError && error.type === 'window_closed') return;
        console.error('[Nango] OAuth auth failed:', error);
        const msg = error instanceof AuthError ? error.message : 'Failed to connect integration';
        toast.error(msg);
      }
    },
    [sessionTokenMutation, queryClient, toast]
  );

  const connectWithCredentials = useCallback(
    async (integration: NangoIntegration, result: CredentialsResult) => {
      setCredentialsLoading(true);
      try {
        const token = await sessionTokenMutation.mutateAsync(undefined);
        const nango = new Nango({ connectSessionToken: token });

        await nango.auth(integration.uniqueKey, {
          credentials: result.credentials,
          params: result.params,
        });
        void queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
        toast.success('Integration connected — select syncs to activate');
        setCredentialsTarget(null);
      } catch (error: unknown) {
        console.error('[Nango] Credentials auth failed:', error);
        const msg = error instanceof AuthError ? error.message : 'Failed to connect — check your credentials';
        toast.error(msg);
      } finally {
        setCredentialsLoading(false);
      }
    },
    [sessionTokenMutation, queryClient, toast]
  );

  const handlePickerSelect = useCallback(
    (integration: NangoIntegration) => {
      const kind = getAuthKind(integration.provider);
      if (kind === 'oauth') {
        connectWithOAuth(integration);
      } else {
        setCredentialsTarget(integration);
      }
    },
    [connectWithOAuth]
  );

  const handleCreateSyncTable = useCallback(
    (connectionId: string, providerConfigKey: string, syncName: string) => {
      createFromConnection.mutate(
        { connectionId, providerConfigKey, syncNames: [syncName] },
        {
          onSuccess: () => {
            toast.success(`Table created for "${syncName}" — syncing`);
          },
          onError: (error: Error) => {
            toast.error(error.message);
          },
        }
      );
    },
    [createFromConnection, toast]
  );

  const handleDisconnect = useCallback(
    (connectionId: string, providerConfigKey: string) => {
      disconnectIntegration.mutate(
        { connectionId, providerConfigKey },
        {
          onSuccess: () => {
            toast.success('Integration disconnected');
          },
          onError: (error: Error) => {
            toast.error(error.message);
          },
        }
      );
    },
    [disconnectIntegration, toast]
  );

  const handleDisconnectNative = useCallback(
    (connectionId: string) => {
      disconnectNativeConnection.mutate(connectionId, {
        onSuccess: () => {
          toast.success('Integration disconnected');
        },
        onError: (error: Error) => {
          toast.error(error.message);
        },
      });
    },
    [disconnectNativeConnection, toast]
  );

  const handleReconnectNative = useCallback(
    (provider: string) => {
      if (provider === 'hubspot') {
        startHubspotOAuth();
        toast.info('Re-authenticating — please approve in the popup');
      }
    },
    [startHubspotOAuth, toast]
  );

  const handleCustomConnectorSelect = useCallback((type: ConnectorType) => {
    if (type === 'http_fetch') setHttpFetchOpen(true);
    else if (type === 'sql') setSqlFetchOpen(true);
    else if (type === 'post_api') setPostApiOpen(true);
    else if (type === 'hubspot_direct') startHubspotOAuth();
  }, [startHubspotOAuth]);

  const handleActivateNativeSync = useCallback(
    (connectionId: string, syncKey: string) => {
      // Currently only HubSpot is a native provider; when adding SFDC,
      // dispatch based on provider here (look up from connectionId).
      batchCreateHubspot.mutate(
        { connectionId, objectTypes: [syncKey as HubspotObjectType] },
        {
          onSuccess: () => toast.success('Table created - syncing'),
          onError: (error: Error) => toast.error(error.message),
        },
      );
    },
    [batchCreateHubspot, toast]
  );

  const makeConnectorSubmitHandler = useCallback(
    (type: 'http_fetch' | 'sql' | 'post_api', label: string, setOpen: (v: boolean) => void) =>
      (data: { name: string; config: Record<string, unknown>; schedule?: string }) => {
        createCustomConnector.mutate(
          { type, name: data.name, config: data.config, schedule: data.schedule },
          {
            onSuccess: () => {
              toast.success(`${label} created`);
              setOpen(false);
            },
            onError: (error: Error) => toast.error(error.message),
          }
        );
      },
    [createCustomConnector, toast]
  );

  const httpFetchSubmit = makeConnectorSubmitHandler('http_fetch', 'HTTP connector', setHttpFetchOpen);
  const sqlFetchSubmit = makeConnectorSubmitHandler('sql', 'SQL connector', setSqlFetchOpen);
  const postApiSubmit = makeConnectorSubmitHandler('post_api', 'POST API table', setPostApiOpen);

  if (membershipLoading) return null;

  if (!canEdit) {
    return (
      <AccessRestricted
        description="Only workspace owners and editors can manage integrations. Contact your workspace owner for assistance."
      />
    );
  }

  return (
    <div className="absolute inset-4 top-0 flex flex-col overflow-hidden">
      <div className="mx-auto flex min-h-0 w-full max-w-4xl flex-1 flex-col gap-4">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Integrations</h1>
            <p className="text-muted-foreground">
              Configure how data flows into your tables from external sources.
            </p>
          </div>
          <Button onClick={() => setPickerOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Integration
          </Button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <IntegrationsList
            tables={tables}
            connectors={connectors}
            nangoConnections={nangoConnections}
            integrationConnections={integrationConnections}
            loading={tablesLoading || connectorsLoading}
            error={tablesError || connectorsError}
            onNangoActivate={handleCreateSyncTable}
            onNangoDisconnect={handleDisconnect}
            onNativeActivate={handleActivateNativeSync}
            onNativeDisconnect={handleDisconnectNative}
            onNativeReconnect={handleReconnectNative}
          />
        </div>
      </div>

      <IntegrationPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={handlePickerSelect}
        onCustomConnectorSelect={handleCustomConnectorSelect}
        connectedProviderKeys={connectedProviderKeys}
        connectedIntegrationProviders={connectedIntegrationProviders}
      />

      <CredentialsDialog
        open={!!credentialsTarget}
        onOpenChange={(open) => { if (!open) setCredentialsTarget(null); }}
        integration={credentialsTarget}
        authKind={credentialsTarget ? (getAuthKind(credentialsTarget.provider) as 'api_key' | 'basic') : 'api_key'}
        loading={credentialsLoading}
        onSubmit={connectWithCredentials}
      />

      <HttpFetchDialog
        open={httpFetchOpen}
        onOpenChange={setHttpFetchOpen}
        loading={createCustomConnector.isPending}
        onSubmit={httpFetchSubmit}
      />
      <SqlFetchDialog
        open={sqlFetchOpen}
        onOpenChange={setSqlFetchOpen}
        loading={createCustomConnector.isPending}
        onSubmit={sqlFetchSubmit}
      />
      <PostApiDialog
        open={postApiOpen}
        onOpenChange={setPostApiOpen}
        loading={createCustomConnector.isPending}
        onSubmit={postApiSubmit}
      />
    </div>
  );
}
